/**
 * Framework-free OOUI world registry.
 *
 * Vendored port of reactdashboard's CopilotRail/ooui/world kernel (and the
 * rebelboost-dashboard lib/ooui equivalent) minus the zod dependency: methods
 * declare plain JSON-schema `parameters` and an optional `validate(input)`
 * guard instead. When `@rebelmouseai/ooui` is published to the registry this
 * module should collapse into it.
 */

export const UNAVAILABLE_OBJECT_RETRY_DELAY_MS = 4000;

class ObjectUnavailableError extends Error {}

export function isObjectUnavailableError(error) {
  return error instanceof ObjectUnavailableError;
}

export function retryOnceAfterDelay(operation, shouldRetry, delayMs) {
  return Promise.resolve()
    .then(operation)
    .catch(error => {
      if (!shouldRetry(error)) {
        throw error;
      }

      return new Promise(resolve => setTimeout(resolve, delayMs)).then(
        operation
      );
    });
}

export function defineMethod(definition) {
  const { description, parameters, method } = definition;
  if (!description || !parameters || typeof method !== 'function') {
    throw new Error(
      'A method needs a description, JSON-schema parameters, and a method function'
    );
  }
  return definition;
}

export function createWorld() {
  const registry = new Map();
  const listeners = new Set();
  let version = 0;

  function notify() {
    version += 1;
    listeners.forEach(listener => listener());
  }

  function invoke(id, method, input) {
    const object = registry.get(id);
    if (!object) {
      throw new ObjectUnavailableError(`Object "${id}" is no longer available`);
    }
    const definition = object.methods[method];
    if (!definition) {
      throw new ObjectUnavailableError(
        `Method "${method}" is no longer available`
      );
    }
    if (definition.validate) {
      definition.validate(input == null ? {} : input);
    }
    return definition.method(input == null ? {} : input);
  }

  return {
    registry,
    register(object) {
      registry.set(object.id, object);
      notify();
    },
    unregister(id) {
      if (registry.delete(id)) {
        notify();
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getVersion() {
      return version;
    },
    execute(id, method, input) {
      return retryOnceAfterDelay(
        () => invoke(id, method, input),
        isObjectUnavailableError,
        UNAVAILABLE_OBJECT_RETRY_DELAY_MS
      );
    },
  };
}
